import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid, PrintPortal, ProjectInput, RichSummaryReport, defineFields, generateShareableURL, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './analysis';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, DEFAULT_VALUES, LIVE_PLOT_SIZE, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import type { ChartKey, FragmentTrajectoryResultState, FragmentTrajectorySavedResult } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';

export default function FragmentTrajectoryApp() {
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);
  const initialValues = useMemo<FormValues>(() => mergeInitialValues(DEFAULT_VALUES, urlFormValues), [urlFormValues]);
  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });
  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, FragmentTrajectorySavedResult>(APP_NAME, APP_VERSION);
  async function saveCompactResult(project: string, title: string, inputs: FormValues, result?: FragmentTrajectoryResultState): Promise<number> {
    if (!result?.summary.html) throw new Error('Run the trajectory analysis before saving.');

    const image = result.plotData?.trajectory ? await chartRefs.current.trajectory?.createImage({ ...PRINT_CHART_IMAGE_OPTIONS, scale: 1 }) : result.charts?.trajectory;
    if (!image) throw new Error('The trajectory chart is not ready to save.');

    return saveDataToDB(project, title, inputs, { summary: result.summary, charts: { trajectory: image } });
  }
  const { analysisResult, setAnalysisResult, analysisError, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, FragmentTrajectoryResultState>({ errors, values, schema, analyze, saveDataToDB: saveCompactResult });
  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, FragmentTrajectoryResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: () => PRINT_CHART_IMAGE_OPTIONS,
    errorMessage: 'Unable to prepare the fragment trajectory chart for printing. Wait for the chart to finish rendering and try again.',
    enableScreenshot: true
  });

  useSavedCalculationLoader<FormValues, FragmentTrajectoryResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Calculation title', placeholder: 'Fragment Trajectory Simulator' },
    { kind: 'input', name: 'rho0', label: 'Air Density', unit: 'lbf·ms²/in⁴', helpText: 'Standard sea-level air density is approximately 0.115' },
    { kind: 'input', name: 'h0', label: 'Launch Height', unit: 'ft', helpText: 'Height above the ground; expressions are accepted' },
    { kind: 'input', name: 'v0', label: 'Launch Velocity', unit: 'ft/s', helpText: 'Nominal launch velocity; expressions are accepted' },
    { kind: 'input', name: 'angles', label: 'Launch Angles', unit: '°', helpText: 'Comma-separated angles and start:step:end ranges' },
    { kind: 'input', name: 'Wt', label: 'Fragment Weight', unit: 'lbf', helpText: 'Fragment weight; expressions are accepted' },
    { kind: 'input', name: 'AD', label: 'Drag Area', unit: 'in²', helpText: 'Presented drag area; expressions are accepted' },
    { kind: 'input', name: 'CD', label: 'Drag Coefficient', helpText: 'Dimensionless drag coefficient; expressions are accepted' },
    { kind: 'input', name: 'FS', label: 'DDESB TP-13 Distance Factor', helpText: 'A distance factor; the default is 1.30' },
    { kind: 'input', name: 'target', label: 'Target', unit: 'ft', helpText: 'Enter standoff or standoff,height,width' }
  ]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Drag and gravity trajectory simulation with DDESB factored-distance design</p>
          </div>
        </div>

        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />
          <FormFields<FormValues> fields={fields} register={register} errors={errors} />
          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button type="button" className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button type="button" className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || !analysisResult || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
          {analysisError ? (
            <div role="alert" aria-live="assertive" className="mx-1 mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
              <span className="font-semibold">Analysis failed:</span> {analysisError}
            </div>
          ) : null}
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1 min-w-0 overflow-hidden">{analysisResult?.summary.html ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">No analysis results yet.</div>}</div>
          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData ? (
              PRINT_CHART_ORDER.map(key => (
                <div key={key} className="m-2 flex min-w-0 items-center justify-center sm:m-4">
                  <LivePlot
                    ref={node => {
                      chartRefs.current[key] = node;
                    }}
                    plot={analysisResult.plotData?.[key]}
                    {...LIVE_PLOT_SIZE}
                  />
                </div>
              ))
            ) : analysisResult?.charts?.trajectory ? (
              <div className="m-2 flex min-w-0 items-center justify-center sm:m-4">
                <img src={`data:${analysisResult.charts.trajectory.mime};base64,${analysisResult.charts.trajectory.base64}`} alt="Saved fragment trajectory chart" className="h-auto w-full max-w-[74rem]" />
              </div>
            ) : null}
          </div>
        </div>
        <div className="print:hidden hidden lg:block" />
      </div>

      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape box-border bg-white p-[1mm]">
            <div className="print-page-content hidden print:grid h-full min-h-0 grid-cols-[28%_72%] border border-black print:bg-white print:text-black">
              <div className="flex h-full min-h-0 flex-col overflow-hidden p-1">
                <div className="text-[10px] font-semibold leading-none">{values.title || 'Fragment Trajectory Simulator'}</div>
                <div className="text-[9px] opacity-70 leading-none mt-0.5 mb-1">Project: {values.project || '—'}</div>
                <div className="mt-2 min-h-0 flex-1 overflow-hidden border-t pt-1">
                  <RichSummaryReport summary={analysisResult?.summary} className="text-[10.0px] leading-[1.15] text-black" />
                </div>
                <div className="grid grid-cols-2 mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold" target="_blank" rel="noopener noreferrer">
                    🔗 LINK
                  </a>
                  <div className="text-[10px] font-semibold leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>
              <div className="h-full min-h-0 overflow-hidden border-l">
                <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} fitPage />
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
